import { useCallback, useEffect, useState } from "react";
import { planApi } from "../../data/api/PlanApi";
import { questionnaireApi } from "../../data/api/QuestionnaireApi";
import {
  Plan,
  PlanAdminPayload,
  PlanCapacityPayload,
  PlanResource,
} from "../../data/models/Plan/PlanModel";
import { Questionnaire } from "../../data/models/Questionnaire/QuestionnaireModel";

const PLAN_PRIORITY = ["MY_KIT", "PLAN_PRO"];

const sortPlans = (plans: Plan[]) =>
  [...plans].sort((a, b) => {
    const indexA = PLAN_PRIORITY.indexOf(a.code ?? "");
    const indexB = PLAN_PRIORITY.indexOf(b.code ?? "");

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });

export function useAdminPlansPage(includeQuestionnaires = false) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [questionnairesLoading, setQuestionnairesLoading] = useState(
    includeQuestionnaires,
  );

  const fetchPlans = useCallback(async () => {
    try {
      setPlansLoading(true);
      const { data, isSuccess } = await planApi.allPlans.request(null);
      const incomingPlans = isSuccess && Array.isArray(data) ? data : [];
      setPlans(sortPlans(incomingPlans));
    } finally {
      setPlansLoading(false);
    }
  }, []);

  const fetchQuestionnaires = useCallback(async () => {
    if (!includeQuestionnaires) {
      return;
    }

    try {
      setQuestionnairesLoading(true);
      const { data, isSuccess } = await questionnaireApi.adminList.request(
        null,
      );
      setQuestionnaires(isSuccess && Array.isArray(data) ? data : []);
    } finally {
      setQuestionnairesLoading(false);
    }
  }, [includeQuestionnaires]);

  const refresh = useCallback(async () => {
    const tasks = [fetchPlans()];
    if (includeQuestionnaires) {
      tasks.push(fetchQuestionnaires());
    }
    await Promise.all(tasks);
  }, [fetchPlans, fetchQuestionnaires, includeQuestionnaires]);

  const pushNewPlanResource = useCallback(
    async (resource: PlanResource) => {
      await planApi.pushNewResource.request({ resource });
      await fetchPlans();
    },
    [fetchPlans],
  );

  const createPlan = useCallback(
    async (payload: PlanAdminPayload) => {
      const result = await planApi.createPlan.request(payload);
      if (!result.isSuccess) {
        throw new Error(result.messageError ?? "No se pudo crear el plan");
      }
      await fetchPlans();
    },
    [fetchPlans],
  );

  const updatePlan = useCallback(
    async (payload: PlanAdminPayload) => {
      const result = await planApi.updatePlan.request(payload);
      if (!result.isSuccess) {
        throw new Error(result.messageError ?? "No se pudo actualizar el plan");
      }
      await fetchPlans();
    },
    [fetchPlans],
  );

  const createCapacity = useCallback(
    async (payload: PlanCapacityPayload) => {
      const result = await planApi.createCapacity.request(payload);
      if (!result.isSuccess) {
        throw new Error(result.messageError ?? "No se pudo crear la capacidad");
      }
      await fetchPlans();
    },
    [fetchPlans],
  );

  const deletePlanResource = useCallback(
    async (resource: PlanResource) => {
      await planApi.deleteResource.request({ resource });
      await fetchPlans();
    },
    [fetchPlans],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    plans,
    questionnaires,
    loading: plansLoading || questionnairesLoading,
    pushNewPlanResource,
    deletePlanResource,
    createPlan,
    updatePlan,
    createCapacity,
    refresh,
  };
}
